import type { PriceBreakdownResponse } from '@/api/useSupplierReconciliation/usePriceBreakdown'
import { dateFormat } from '@/constants/dateFormat'
import { Texto } from '@gravitate-js/excalibrr'
import type { CellClassParams, ColDef, ICellRendererParams } from 'ag-grid-community'
import dayjs from 'dayjs'

// Format helper
const fmt = {
  decimal: (value: number) => {
    if (value === 0) return '0.0000'
    return value.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
  },
}

export function createPriceBreakdownColumnDefs(): ColDef[] {
  return [
    {
      field: 'display_name',
      headerName: 'Display',
      cellRenderer: ({ value }) => <Texto className='text-truncate'>{value}</Texto>,
    },
    {
      field: 'name',
      headerName: 'Name',
    },
    {
      field: 'value',
      headerName: 'Value',
      formatter: fmt.decimal,
      cellRenderer: (params: { data: { status: string | null }; value: number }) => {
        if (!params.value) return null
        return fmt.decimal(params.value)
      },
      cellStyle: getCellStyle,
    },
    {
      field: 'status',
      headerName: 'Status',
      cellStyle: getCellStyle,
    },
    {
      field: 'type',
      headerName: 'Type',
    },
    {
      field: 'description',
      headerName: 'Description',
    },
    {
      field: 'as_of',
      headerName: 'As of Date',
      minWidth: 140,
      cellRenderer: (params: ICellRendererParams<PriceBreakdownResponse>) =>
        params.value ? dayjs.utc(params.value).format(dateFormat.MONTH_DATE_YEAR_TIME) : '',
    },
  ]
}

// Keep the original export for backward compatibility (without timezone)
export const priceBreakdownColumnDefs = createPriceBreakdownColumnDefs()

function getCellStyle(params: CellClassParams) {
  switch (params.data?.status) {
    case 'missing':
      return { backgroundColor: 'var(--theme-error-trans)' }
    case 'optimal':
      return { backgroundColor: 'var(--theme-optimal-dim)' }
    case 'valid':
      return { backgroundColor: 'var(--theme-success-dim)' }
    case 'estimated':
      return { backgroundColor: 'var(--theme-color-1-dim)' }
    default:
      return {}
  }
}
